import React, { useState } from 'react';
import { Button, Checkbox, Modal } from 'ncoded-component-library';
import { unmountComponentAtNode, render } from 'react-dom';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import './ConfirmModal.styles.scss';
import './ConfirmModal.styles.responsive.scss';

type ConfirmConfig = {
  className?: string;
  type?: 'confirm' | 'delete';
  title?: string;
  content?: React.ReactNode;
  submitBtnText?: string;
  cancelBtnText?: string;
  yesNoQuestion?: boolean;
  overlayCloses?: boolean;
  onlyOneOption?: boolean;
  checkboxAgreeLabel?: string;
  onSubmit?: () => void;
  onClose?: () => void;
  onX?: () => void;
};

export type ConfirmObject = {
  simpleConfirm: (conf: ConfirmConfig) => Promise<boolean>;
};

const ConfirmModal: React.FC<ConfirmConfig> = (props) => {
  const {
    type = 'confirm',
    className,
    title,
    content,
    submitBtnText,
    cancelBtnText,
    yesNoQuestion,
    checkboxAgreeLabel,
    onlyOneOption = false,
    onClose,
    onSubmit,
    onX,
    ...rest
  } = props;
  const { t } = useTranslation();

  const [checkboxValue, setCheckboxValue] = useState(false);

  const classes = classNames(
    'confirm-modal',
    `confirm-modal--${type}`,
    className,
  );

  return (
    <Modal
      className={classes}
      title={title}
      footer={
        <>
          {!onlyOneOption && (
            <Button variant="link" onClick={onClose}>
              {cancelBtnText || t(yesNoQuestion ? 'no' : 'cancel')}
            </Button>
          )}
          <Button
            disabled={checkboxAgreeLabel && !checkboxValue}
            onClick={onSubmit}
          >
            {submitBtnText || t(yesNoQuestion ? 'yes' : 'ok')}
          </Button>
        </>
      }
      open
      onClose={onX}
      {...rest}
    >
      {content}
      {checkboxAgreeLabel && (
        <Checkbox
          label={checkboxAgreeLabel}
          checked={checkboxValue}
          onChange={() => setCheckboxValue((prev) => !prev)}
        />
      )}
    </Modal>
  );
};

export default async (config: ConfirmConfig, yesNoQuestion = false) => {
  return new Promise((resolve) => {
    const { title, content, onSubmit, onClose, onX, ...rest } = config;

    const div = document.createElement('div');
    document.body.appendChild(div);

    const cleanup = () => {
      unmountComponentAtNode(div);
      document.body.removeChild(div);
    };

    // eslint-disable-next-line react/no-render-return-value
    return render(
      <ConfirmModal
        title={title}
        content={content}
        onSubmit={() => {
          resolve(true);
          cleanup();
          onSubmit?.();
        }}
        onClose={() => {
          resolve(false);
          cleanup();
          onClose?.();
        }}
        onX={() => {
          resolve(false);
          cleanup();
          onX?.();
        }}
        {...rest}
        yesNoQuestion={yesNoQuestion}
      />,
      div,
    );
  });
};
